import { type ReactNode, Suspense, useEffect, useState } from 'react';

interface DelayedSuspenseProps {
    /** Контент, который загружается асинхронно */
    children: ReactNode;
    /** Fallback, который показывается при загрузке */
    fallback: ReactNode;
    /** Служебный контент, который должен смонтироваться сразу при ожидании, не дожидаясь визуального fallback. */
    immediateFallback?: ReactNode;
    /** Задержка перед показом fallback (ms). По умолчанию 250ms */
    delay?: number;
}

/*
 * DelayedSuspense - обертка над Suspense с задержкой показа fallback.
 *
 * Решает проблему "мелькания" fallback при быстрой загрузке бандлов:
 * - Показывает fallback только если загрузка занимает больше `delay` ms
 * - Если загрузка завершится быстрее delay, fallback вообще не появится
 * - Служебный `immediateFallback` при необходимости монтируется сразу и не зависит от визуальной задержки
 *
 * Рекомендуемое значение delay: 200-300ms (на основе исследований UX)
 */
export const DelayedSuspense = ({ children, fallback, immediateFallback, delay = 250 }: DelayedSuspenseProps) => (
    <Suspense fallback={<DelayedFallback fallback={fallback} immediateFallback={immediateFallback} delay={delay} />}>
        {children}
    </Suspense>
);

interface DelayedFallbackProps {
    fallback: ReactNode;
    immediateFallback?: ReactNode;
    delay: number;
}

const DelayedFallback = ({ fallback, immediateFallback, delay }: DelayedFallbackProps) => {
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShouldRender(true);
        }, delay);

        return () => {
            clearTimeout(timer);
        };
    }, [delay]);

    return (
        <>
            {immediateFallback}
            {shouldRender ? fallback : null}
        </>
    );
};
